import { Link } from "react-router-dom";
import { Clock, Recycle, Truck, BarChart3, Receipt, Users, Check, ArrowRight, Store } from "lucide-react";
import { PublicNav } from "@/components/PublicNav";

const BANNER = "https://images.unsplash.com/photo-1628102491629-778571d893a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMGZvb2QlMjBncm9jZXJ5JTIwcHJvZHVjZXxlbnwwfHx8fDE3ODI5MjIwMDl8MA&ixlib=rb-4.1.0&q=85";

const outcomes = [
  { icon: Clock, title: "Save employee time", desc: "No more staff hours culling, sorting, and hauling near-expiry stock to the compactor." },
  { icon: Recycle, title: "Reduce disposal costs", desc: "Divert edible food from dumpsters and landfill tipping fees to local aid organizations." },
  { icon: Truck, title: "Zero-effort pickups", desc: "Vetted organizations come to you. Post what's available; they claim and collect it." },
  { icon: BarChart3, title: "ESG reporting", desc: "Track pounds rescued, meals provided, and carbon avoided — audit-ready for stakeholders." },
  { icon: Receipt, title: "Tax documentation", desc: "Every donation is logged with value estimates for your charitable-contribution deductions." },
  { icon: Users, title: "Local community impact", desc: "Show customers real, measurable good happening in their own neighborhood." },
];

export default function ForGroceryStores() {
  return (
    <div className="min-h-screen bg-stone-50">
      <PublicNav />

      <section className="pt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs tracking-widest uppercase text-orange-700 font-medium">For grocery executives</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900 mt-4 leading-tight">
              Stop paying to throw away good food.
            </h1>
            <p className="text-lg text-stone-600 mt-5 leading-relaxed">
              Gather turns your near-expiry and surplus inventory from a cost center into measurable community impact —
              with almost zero effort from your team, and full documentation for ESG and taxes.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link data-testid="enterprise-cta" to="/register?role=grocery_store" className="inline-flex items-center gap-2 bg-emerald-800 text-white rounded-full px-8 py-3.5 font-medium hover:bg-emerald-900 transition-all hover:-translate-y-0.5">
                <Store className="w-4 h-4" /> Start rescuing food
              </Link>
            </div>
          </div>
          <img src={BANNER} alt="Grocery store" className="rounded-3xl w-full h-full object-cover min-h-[320px] shadow-sm" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {outcomes.map((o) => (
            <div key={o.title} className="bg-white rounded-2xl border border-stone-200 p-7 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><o.icon className="w-5 h-5 text-emerald-800" /></div>
              <h3 className="font-heading text-lg font-medium text-stone-900 mt-5">{o.title}</h3>
              <p className="text-stone-600 mt-2 text-sm leading-relaxed">{o.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="max-w-xl mx-auto bg-emerald-900 text-emerald-50 rounded-3xl p-10 text-center shadow-lg">
          <span className="text-xs tracking-widest uppercase text-emerald-300/80">Simple pricing</span>
          <div className="flex items-baseline justify-center gap-2 mt-4">
            <span className="font-heading text-6xl font-semibold text-white">$499</span>
            <span className="text-emerald-200/80">/ store / month</span>
          </div>
          <p className="font-heading text-xl text-white mt-2">Unlimited food rescue.</p>
          <ul className="mt-6 space-y-2 text-left max-w-sm mx-auto">
            {["Unlimited monthly food rescue", "Zero-effort scheduled pickups", "ESG & impact reporting", "Tax documentation", "100% of your subscription donated to Partnership to End Addiction"].map((f) => (
              <li key={f} className="flex items-start gap-2 text-emerald-100/90 text-sm"><Check className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" /> {f}</li>
            ))}
          </ul>
          <Link data-testid="pricing-cta" to="/register?role=grocery_store" className="inline-flex items-center gap-2 bg-white text-emerald-900 rounded-full px-8 py-3.5 font-medium mt-8 hover:bg-emerald-50 transition-all hover:-translate-y-0.5">
            Start rescuing food <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
